// src/services/feedSignals.js — the browser's half of the ledger the
// recommender learns from. The mirror of the app's services/feedSignals.ts,
// deliberately the same shape so the two feeds learn the same way.
//
// 🟥 EVERY EVENT IS ABOUT A POST, AND NOTHING ELSE CAN BE PUT IN ONE. There is
//    no field here in which a chat, a contact or a call could arrive — not
//    filtered out on the server, impossible to send. The feed learns from the
//    feed. A boundary that depends on somebody remembering to filter is a
//    boundary that fails on the next edit, and this is exactly the file where a
//    "while we are here, also send…" would one day be added.
//
// 🟥 BUFFERED, NEVER PER-EVENT, and flushed on `visibilitychange` and `pagehide`
//    rather than `beforeunload` — which does not fire on mobile Safari at all,
//    and is precisely the session whose signals matter most.
//
// 🟢 AND THE FINAL FLUSH USES sendBeacon WHERE IT EXISTS. A fetch started as a
//    tab closes is cancelled with the tab; a beacon is handed to the browser to
//    deliver afterwards. It cannot carry an Authorization header, so the token
//    rides in the body and the route accepts either.

import { auth } from '../config/firebase';

const BACKEND = process.env.REACT_APP_BACKEND_URL
  || 'https://signaling-server-prod-397203671414.us-central1.run.app';

const STORE_KEY = 'feed.signals.pending.v1';
const FLUSH_AFTER_MS = 20000;
const FLUSH_AT = 20;
const MAX_PENDING = 300;

let pending = load();
let timer = null;
let sending = false;

function load() {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return Array.isArray(saved) ? saved.slice(-MAX_PENDING) : [];
  } catch { return []; }
}

function persist() {
  try { window.localStorage.setItem(STORE_KEY, JSON.stringify(pending)); } catch { /* private mode */ }
}

/* 🟢 THE SAME EVENT TWICE IS ONE EVENT, and the bigger one wins. A post dwelt
   on for two seconds and then, after a scroll back, for eight is one eight-
   second dwell — summing them would let one post teach the profile twice as
   hard as it earned, and the server cannot tell the difference afterwards.
   Replays are the exception: they are a COUNT, so they add. */
function merge(list, ev) {
  const cumulative = ev.type === 'replay';
  const i = list.findIndex((x) => x.postId === ev.postId && x.type === ev.type);
  if (i < 0) return [...list, ev];
  const prev = list[i];
  const next = cumulative
    ? { ...prev, m: (Number(prev.m) || 0) + (Number(ev.m) || 1), at: ev.at }
    : { ...prev, ...ev, m: Math.max(Number(prev.m) || 0, Number(ev.m) || 0) };
  const out = [...list];
  out[i] = next;
  return out;
}

/**
 * Send what is queued.
 *
 * 🟥 THE BUFFER IS CLEARED BEFORE THE REQUEST AND PUT BACK ON FAILURE. Clearing
 *    afterwards means a slow response and a second flush send the same events
 *    twice, and an event carries no id with which the server could notice.
 */
export async function flush() {
  if (sending || !pending.length) return;
  const u = auth.currentUser;
  if (!u) return;
  const batch = pending;
  pending = [];
  sending = true;
  persist();
  try {
    const idToken = await u.getIdToken();
    const res = await fetch(`${BACKEND}/api/feed/signals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
    if (!res.ok) throw new Error(String(res.status));
  } catch {
    pending = [...batch, ...pending].slice(-MAX_PENDING);
    persist();
  } finally {
    sending = false;
  }
}

function schedule() {
  if (timer) return;
  timer = setTimeout(() => { timer = null; flush(); }, FLUSH_AFTER_MS);
}

/** Record one event. Never throws, never awaited by a UI path. */
export function record(ev) {
  if (!ev || !ev.postId || !ev.type) return;
  try {
    pending = merge(pending, { ...ev, at: ev.at || Date.now() }).slice(-MAX_PENDING);
    persist();
    if (pending.length >= FLUSH_AT) flush(); else schedule();
  } catch { /* telemetry must never be able to break a feed */ }
}

/* ── the vocabulary, so no component has to know the wire format ───────── */
export const watched = (postId, ratio, durationSec) =>
  record({ type: 'completion', postId, m: Math.max(0, Math.min(1, ratio || 0)), durationSec });
export const replayed = (postId) => record({ type: 'replay', postId, m: 1 });
export const swipedAway = (postId, ms) => record({ type: 'swipe_away', postId, m: Math.max(0, ms || 0) });
export const dwelled = (postId, ms) => record({ type: 'dwell', postId, m: Math.max(0, ms || 0) });
export const unmuted = (postId) => record({ type: 'unmute', postId });
export const expandedCaption = (postId) => record({ type: 'expand_caption', postId });
export const openedComments = (postId) => record({ type: 'open_comments', postId });
export const liked = (postId) => record({ type: 'like', postId });
export const commented = (postId) => record({ type: 'comment', postId });
export const shared = (postId) => record({ type: 'share', postId });
export const saved = (postId) => record({ type: 'save', postId });
export const visitedProfile = (postId) => record({ type: 'profile_visit', postId });
export const followed = (postId) => record({ type: 'follow', postId });
export const notInterested = (postId) => record({ type: 'not_interested', postId });
export const hidden = (postId) => record({ type: 'hide', postId });
export const reported = (postId) => record({ type: 'report', postId });

/* ══ ⏱ WATCHING, AS OPPOSED TO CLICKING ═══════════════════════════════════
   🟥 THE STRONGEST SIGNALS ARE THE ONES NOBODY PERFORMS. Likes are sparse and
      strategic; how long somebody watched is neither. It also cannot be
      recorded by a component alone — it exists only in the difference between
      two moments, and the second is a scroll that has already happened. */
const watching = new Map();
const LOOP_BACK_MS = 1200;

export const watch = {
  start(postId) {
    if (!postId || watching.has(postId)) return;
    watching.set(postId, { startedAt: Date.now(), maxPos: 0, lastPos: 0, durationMs: 0, loops: 0 });
  },
  progress(postId, positionSec, durationSec) {
    const w = watching.get(postId);
    if (!w) return;
    const pos = (Number(positionSec) || 0) * 1000;
    if (durationSec && !w.durationMs) w.durationMs = (Number(durationSec) || 0) * 1000;
    if (pos + LOOP_BACK_MS < w.lastPos) w.loops += 1;
    w.lastPos = pos;
    if (pos > w.maxPos) w.maxPos = pos;
  },
  end(postId) {
    const w = watching.get(postId);
    if (!w) return;
    watching.delete(postId);
    const elapsed = Date.now() - w.startedAt;
    const durationSec = w.durationMs ? Math.round(w.durationMs / 1000) : 0;
    const ratio = w.durationMs ? Math.min(1, (w.maxPos + w.loops * w.durationMs) / w.durationMs) : 0;
    if (durationSec) watched(postId, ratio, durationSec);
    for (let i = 0; i < Math.min(w.loops, 3); i += 1) replayed(postId);
    /* 🟥 A SKIP IS ONLY A SKIP IF THEY DID NOT SEE IT. Leaving after nearly all
       of a clip is the clip ending, not a rejection. */
    if (elapsed < 6000 && ratio < 0.8 && !w.loops) swipedAway(postId, elapsed);
  },
  setActive(postId) {
    for (const id of [...watching.keys()]) if (id !== postId) watch.end(id);
    if (postId) watch.start(postId);
  },
  /* 🟥 `send` IS A PARAMETER BECAUSE THE CLOSING PATH MUST NOT FLUSH. A
     fetch begun as a tab closes is cancelled with the tab; the closing path
     ends the measurements and then hands everything to `sendBeacon` in one
     go. Without this the final flush finds an empty buffer and the beacon —
     the entire point of the closing path — sends nothing. */
  endAll(send = true) {
    for (const id of [...watching.keys()]) watch.end(id);
    if (send) flush();
  },
};

/* ══ 👀 DWELL — the main feed's version of the same question ═══════════════
   🟢 A PHOTO HAS NO PLAYHEAD, so "how much of it did they see" means nothing;
      how long it stayed on screen is the whole of what there is to know. */
const dwellingSince = new Map();

export const dwell = {
  setVisible(ids) {
    const now = Date.now();
    const next = new Set((ids || []).filter(Boolean));
    for (const [id, since] of [...dwellingSince.entries()]) {
      if (next.has(id)) continue;
      dwellingSince.delete(id);
      dwelled(id, now - since);
    }
    for (const id of next) if (!dwellingSince.has(id)) dwellingSince.set(id, now);
  },
  clear(send = true) {
    const now = Date.now();
    for (const [id, since] of [...dwellingSince.entries()]) dwelled(id, now - since);
    dwellingSince.clear();
    if (send) flush();
  },
};

/**
 * The tab is going away.
 *
 * 🟥 `visibilitychange` AND `pagehide`, NOT `beforeunload`. beforeunload does
 *    not fire on iOS Safari when a tab is discarded or the app is swiped away —
 *    which is the ordinary way a phone browsing session ends, and so the exact
 *    session whose last twenty seconds we would always lose.
 */
function finalFlush() {
  try {
    dwell.clear(false);
    watch.endAll(false);
    if (!pending.length) return;
    const u = auth.currentUser;
    if (!u || !navigator.sendBeacon) { flush(); return; }
    /* 🟢 a beacon cannot carry an Authorization header, so the token goes in the
       body — the same token, checked the same way, in a request the browser
       finishes delivering after this page is gone */
    u.getIdToken().then((idToken) => {
      const body = JSON.stringify({ events: pending, idToken });
      const ok = navigator.sendBeacon(`${BACKEND}/api/feed/signals`, new Blob([body], { type: 'application/json' }));
      if (ok) { pending = []; persist(); }
    }).catch(() => { /* the buffer survives on disk for the next visit */ });
  } catch { /* a page unload is never the place to throw */ }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') finalFlush(); });
  window.addEventListener('pagehide', finalFlush);
}
